import { Userpilot } from "userpilot";
import { IS_POC, IS_SELF_HOSTED, USERPILOT_TOKEN } from "@/constants/constants";
import { clearVendorStorage } from "@/services/consent/storage-cleanup";

const USERPILOT_STORAGE_PREFIXES = ["userpilot"] as const;

export interface UserpilotUser {
	id: string;
	name: string;
	email?: string;
	companyId?: string;
	companyName?: string;
	createdAt?: number;
}

let isInitialized = false;

/**
 * Userpilot only runs where the build supplies `VITE_APP_USERPILOT_TOKEN`,
 * and never self-hosted or in a POC.
 */
const isUserpilotEnabled = () =>
	Boolean(USERPILOT_TOKEN) && !IS_SELF_HOSTED && !IS_POC;

/** Same contract as the Clarity wrapper: no-op until init, never throws. */
const withUserpilot = (action: () => void) => {
	if (!isInitialized) return;
	try {
		action();
	} catch {
		// Intentionally ignored: product guidance must never break the app.
	}
};

/**
 * Loads Userpilot. Only the consent service calls this, once the user has
 * accepted the functional category. Repeat calls are no-ops.
 */
const initUserpilot = () => {
	if (isInitialized || !isUserpilotEnabled()) return;

	try {
		Userpilot.initialize(USERPILOT_TOKEN);
		isInitialized = true;
	} catch {
		// Intentionally ignored: an ad-blocker or a CDN failure is not an app error.
	}
};

const identifyUserpilotUser = (user: UserpilotUser) =>
	withUserpilot(() => {
		Userpilot.identify(user.id, {
			name: user.name,
			email: user.email,
			company: { id: user.companyId, name: user.companyName },
			created_at: user.createdAt,
		});
	});

/** Re-evaluates Userpilot content for the current page. */
const reloadUserpilot = () =>
	withUserpilot(() => {
		Userpilot.reload();
	});

/** Shuts Userpilot down and deletes the identity it stored. */
const teardownUserpilot = () => {
	withUserpilot(() => {
		Userpilot.destroy();
	});
	clearVendorStorage(USERPILOT_STORAGE_PREFIXES);
	isInitialized = false;
};

export {
	identifyUserpilotUser,
	initUserpilot,
	isUserpilotEnabled,
	reloadUserpilot,
	teardownUserpilot,
};
